import { createLogger } from "@/utils/logger";
import { Airport } from "../models/airport";
import { SearchFlights, SeatClass } from "../models/searchflight";
import { Airline } from "../models/airline";
import { IConfig, config as defaultConfig } from "@/utils/config";
import { IApiKeyHolder, apiKeyHolder } from "@/utils/apikey";
import {
  DEFAULT_ERROR_MESSAGE,
  ERROR_RESPONSES,
  RESPONSE_OK,
  ResponseStatusMessage,
} from "@/core/services/search.service.responses";
import {
  customParametersHolder,
  ICustomParametersHolder,
} from "@/utils/customparameters";

export interface ISearchResponse {
  flightNumber: string;
  aircraftTypeName: string;
  distance: number;
  flightsOver12Months: number;
  aircraftRegistration: string;
  seatClasses: {
    name: string;
    emissionsPerSeat: number;
    seats: number;
    type: SeatClass;
  }[];
  originAirport: Airport;
  destinationAirport: Airport;
  airline: Airline;
  errorMessage: ResponseStatusMessage;
}

export interface ICalculateFootprintResponse {
  cO2PerSeatAll: number;
  cO2PerSeatFirst: number;
  cO2PerSeatBusiness: number;
  cO2PerSeatPremiumEconomy: number;
  cO2PerSeatEconomyComfort: number;
  cO2PerSeatEconomy: number;
  averaged: boolean;
  sourceFlightNumber: string;
  aircraftRegistration: string;
  originAirportCode: string;
  destinationAirportCode: string;
  originCity: string;
  destinationCity: string;
  flightCycles: number;
  distanceKm: number;
  aircraftModel: string;
  seatsEconomy: number;
  seatsBusiness: number;
  seatsFirstClass: number;
  seatsPremiumEconomy: number;
  seatsEconomyComfort: number;
  airline: string;
  airlineIataCode: string;
  errorMessage: ResponseStatusMessage;
}

export interface ISearchService {
  search(searchFlights: SearchFlights): Promise<ISearchResponse>;
}

export class SearchService implements ISearchService {
  private logger = createLogger("SearchService");

  public constructor(
    private readonly _config: IConfig,
    private readonly apiKeyHolder: IApiKeyHolder,
    private readonly customParamentersHolder: ICustomParametersHolder,
  ) {}

  public async search(params: SearchFlights): Promise<ISearchResponse> {
    let response: ICalculateFootprintResponse;
    if (params.basicSearch) {
      response = await this.searchByFlightNumber(params);
    } else {
      response = await this.searchByRoute(params);
    }

    return {
      flightNumber: response.sourceFlightNumber,
      aircraftTypeName: response.aircraftModel,
      distance: response.distanceKm,
      flightsOver12Months: response.flightCycles,
      aircraftRegistration: response.aircraftRegistration,
      seatClasses: [
        {
          name: "First class",
          emissionsPerSeat: response.cO2PerSeatFirst,
          seats: response.seatsFirstClass,
          type: SeatClass.First,
        },
        {
          name: "Business class",
          emissionsPerSeat: response.cO2PerSeatBusiness,
          seats: response.seatsBusiness,
          type: SeatClass.Business,
        },
        {
          name: "Premium economy",
          emissionsPerSeat: response.cO2PerSeatPremiumEconomy,
          seats: response.seatsPremiumEconomy,
          type: SeatClass.PremiumEconomy,
        },
        {
          name: "Comfort economy",
          emissionsPerSeat: response.cO2PerSeatEconomyComfort,
          seats: response.seatsEconomyComfort,
          type: SeatClass.ComfortEconomy,
        },
        {
          name: "Economy",
          emissionsPerSeat: response.cO2PerSeatEconomy,
          seats: response.seatsEconomy,
          type: SeatClass.Economy,
        },
      ],
      airline: { name: response.airline, code: response.airlineIataCode },
      originAirport: {
        name: response.originCity,
        code: response.originAirportCode,
      },
      destinationAirport: {
        name: response.destinationCity,
        code: response.destinationAirportCode,
      },
      errorMessage: response.errorMessage,
    };
  }

  private async searchByFlightNumber(
    params: SearchFlights,
  ): Promise<ICalculateFootprintResponse> {
    this.logger.log(
      `searching flights by flight number with ${JSON.stringify(params)}`,
    );
    const response = await fetch(
      `${this._config.serviceBaseUrl}/co2footprint/calculate?flightNumber=${
        params.flightNumber
      }&applyLoadFactor=${this.customParamentersHolder.isLoadFactorApplicable()}`,
      {
        headers: {
          "x-api-key": this.apiKeyHolder.getApiKey(),
        },
      },
    );
    return await this.handleResponse(response);
  }

  private async searchByRoute(
    params: SearchFlights,
  ): Promise<ICalculateFootprintResponse> {
    this.logger.log(
      `searching flights by route with ${JSON.stringify(params)}`,
    );
    const response = await fetch(
      `${this._config.serviceBaseUrl}/co2footprint/calculate?airline=${
        params.airline
      }&origin=${params.departureAirport}&destination=${
        params.arrivalAirport
      }&applyLoadFactor=${this.customParamentersHolder.isLoadFactorApplicable()}`,
      {
        headers: {
          "x-api-key": this.apiKeyHolder.getApiKey(),
        },
      },
    );
    return await this.handleResponse(response);
  }

  private async handleResponse(
    response: Response,
  ): Promise<ICalculateFootprintResponse> {
    if (response.status === RESPONSE_OK) {
      return await response.json();
    }

    const error = ERROR_RESPONSES.filter(
      (error) => error.statusCode === response.status,
    );
    if (error.length > 0) {
      return <ICalculateFootprintResponse>{
        errorMessage: error[0],
      };
    }
    return <ICalculateFootprintResponse>{
      errorMessage: <ResponseStatusMessage>{
        statusCode: response.status,
        message: DEFAULT_ERROR_MESSAGE,
      },
    };
  }
}

export const searchService = new SearchService(
  defaultConfig,
  apiKeyHolder,
  customParametersHolder,
);
